import React, { useEffect, useRef, useState } from "react";
import { uploadResume, latestResume, buildResume } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, Copy, ChevronDown } from "lucide-react";
import ResumePreview from "@/components/wishire/ResumePreview";
import AtsPanel from "@/components/wishire/AtsPanel";
import CoachingPlan from "@/components/wishire/CoachingPlan";

export default function ResumePage() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [targetRole, setTargetRole] = useState(user?.target_role || "");
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [latest, setLatest] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await latestResume();
        if (data?.result) {
          setLatest(data);
          setResult(data.result);
          setResumeText(data.text || "");
        }
      } catch (e) {}
    })();
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) {
      setError("Please choose a PDF or DOCX file first.");
      return;
    }
    setStatus("uploading");
    setError(null);
    try {
      const { data } = await uploadResume(file, targetRole || undefined);
      setResult(data.resume.result);
      setResumeText(data.resume.text || "");
      setLatest(data.resume);
      setStatus("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Try again.");
      setStatus("error");
    }
  };

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Resume Analyzer</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Sharpen your resume.{" "}
          <span className="italic text-stone-500">Then watch your score climb.</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Upload card */}
        <div className="lg:col-span-5 rounded-3xl bg-white border border-stone-200 p-7">
          <h2 className="font-display text-2xl mb-2">Upload</h2>
          <p className="text-stone-600 text-sm mb-6">
            PDF or DOCX. Max 5MB. We parse the text and score it against your target role.
          </p>

          <input
            type="text"
            data-testid="resume-target-role"
            placeholder="Target role"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            className="w-full mb-4 px-4 py-2.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
          />

          <div
            data-testid="resume-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="rounded-2xl border-2 border-dashed border-stone-300 bg-[#F5F3ED] py-12 px-6 text-center cursor-pointer hover:border-stone-900 transition-colors"
          >
            <Upload className="mx-auto mb-3" size={24} />
            {file ? (
              <p className="text-stone-900 text-sm font-medium">{file.name}</p>
            ) : (
              <>
                <p className="text-stone-900 font-medium">Drop your resume here</p>
                <p className="text-stone-500 text-sm mt-1">or click to browse</p>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={onPick}
              className="hidden"
              data-testid="resume-file-input"
            />
          </div>

          <button
            type="button"
            data-testid="resume-submit"
            onClick={submit}
            disabled={status === "uploading" || !file}
            className="btn-primary w-full justify-center mt-5 disabled:opacity-60"
          >
            {status === "uploading" ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Scoring your resume…
              </>
            ) : (
              <>Score my resume</>
            )}
          </button>

          {error && (
            <p data-testid="resume-error" className="mt-3 text-red-700 text-sm inline-flex items-center gap-2">
              <AlertTriangle size={14} /> {error}
            </p>
          )}
          {status === "done" && (
            <p data-testid="resume-success" className="mt-3 text-emerald-700 text-sm inline-flex items-center gap-2">
              <CheckCircle2 size={14} /> Scored. Scroll to see details.
            </p>
          )}
        </div>

        {/* Result card */}
        <div className="lg:col-span-7 rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-10 relative overflow-hidden wsh-grain">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(500px 300px at 90% 10%, rgba(217,119,6,0.4), transparent 60%)",
            }}
          />
          <div className="relative">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
              Resume score
            </span>
            <div className="mt-3 flex items-end gap-4">
              <h2 className="font-display text-6xl md:text-7xl leading-none" data-testid="resume-overall-score">
                {result?.overall_score ?? "—"}
              </h2>
              <span className="text-stone-400 pb-2">/ 100</span>
              {result?.role_fit && (
                <span className="ml-auto px-3 py-1 rounded-full bg-amber-300 text-stone-900 font-mono uppercase tracking-widest text-[10px]">
                  {result.role_fit}
                </span>
              )}
            </div>

            {result?.summary && (
              <p className="mt-5 text-stone-200 leading-relaxed max-w-2xl" data-testid="resume-summary">
                {result.summary}
              </p>
            )}

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section
                title="Strengths"
                items={result?.strengths || []}
                color="#86EFAC"
              />
              <Section
                title="Gaps"
                items={result?.gaps || []}
                color="#FCD34D"
              />
            </div>

            <div className="mt-6">
              <p className="overline mb-3" style={{ color: "rgba(253,251,247,0.55)" }}>
                Suggested improvements
              </p>
              <ol className="space-y-3" data-testid="resume-improvements">
                {(result?.suggested_improvements || []).map((s, i) => (
                  <li key={i} className="flex gap-3 text-stone-100 leading-relaxed">
                    <span className="font-display text-amber-300 text-xl shrink-0 w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
                {!result && (
                  <li className="text-stone-400 text-sm">
                    Upload a resume to see your improvement plan.
                  </li>
                )}
              </ol>
            </div>

            {result && (
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                <Stat label="ATS Score" value={result.ats_score} />
                <Stat label="Keyword Match" value={result.keyword_match} />
              </div>
            )}
            {result?.ats && <div className="mt-6"><AtsPanel ats={result.ats} dark /></div>}
          </div>
        </div>
      </div>

      {result?.line_rewrites?.length > 0 && (
        <LineRewritesSection rewrites={result.line_rewrites} />
      )}

      {resumeText && (
        <ResumeBuilderInline resumeText={resumeText} targetRole={targetRole} />
      )}

      {latest?.filename && (
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-stone-600">
          <FileText size={14} /> Last analyzed: {latest.filename}
        </div>
      )}

      {/* Coaching plan — appears after first score */}
      {result && (
        <div className="mt-10">
          <div className="mb-5">
            <span className="overline">AI Coaching</span>
            <h2 className="font-display text-3xl mt-2">
              Here's how to close your gaps.
            </h2>
            <p className="text-stone-600 mt-1">
              Your placement plan — the next steps to tick off, with links into the right tool for each.
            </p>
          </div>
          <CoachingPlan autoLoad />
        </div>
      )}
    </div>
  );
}

function Section({ title, items, color }) {
  return (
    <div>
      <p className="overline mb-3" style={{ color: "rgba(253,251,247,0.55)" }}>
        {title}
      </p>
      <ul className="space-y-2">
        {items.length === 0 ? (
          <li className="text-stone-400 text-sm">—</li>
        ) : (
          items.map((s, i) => (
            <li key={i} className="text-stone-100 leading-relaxed flex gap-2">
              <span style={{ color }}>•</span>
              <span>{s}</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
      <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
        {label}
      </p>
      <p className="font-display text-3xl text-stone-50 mt-1">{value ?? 0}</p>
    </div>
  );
}

function LineRewritesSection({ rewrites }) {
  const [copied, setCopied] = useState({});

  const copy = (i, text) => {
    navigator.clipboard.writeText(text);
    setCopied(c => ({ ...c, [i]: true }));
    setTimeout(() => setCopied(c => ({ ...c, [i]: false })), 2000);
  };

  return (
    <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-amber-100 text-[#C2410C]">
          <Sparkles size={18} />
        </div>
        <div>
          <h2 className="font-display text-xl">AI-suggested line edits</h2>
          <p className="text-sm text-stone-500 mt-0.5">{rewrites.length} improvements found — click any rewrite to copy</p>
        </div>
      </div>
      <div className="space-y-4">
        {rewrites.map((r, i) => (
          <div key={i} className="rounded-2xl border border-stone-100 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-100">
              <div className="p-4 bg-red-50">
                <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-2">Before</p>
                <p className="text-sm text-stone-700 leading-relaxed">{r.original}</p>
              </div>
              <div className="p-4 bg-emerald-50">
                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 mb-2">After</p>
                <p className="text-sm text-stone-800 leading-relaxed font-medium">{r.rewritten}</p>
              </div>
            </div>
            <div className="px-4 py-2.5 bg-stone-50 border-t border-stone-100 flex items-center justify-between gap-3">
              <p className="text-xs text-stone-500 flex-1">{r.reason}</p>
              <button
                onClick={() => copy(i, r.rewritten)}
                className="shrink-0 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors"
              >
                {copied[i] ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                {copied[i] ? "Copied" : "Copy rewrite"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResumeBuilderInline({ resumeText, targetRole }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [built, setBuilt] = useState(null);

  const handleBuild = async () => {
    setLoading(true);
    try {
      const { data } = await buildResume(resumeText, targetRole);
      setBuilt(data);
    } catch (e) {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 rounded-3xl border border-stone-200 bg-white overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-8 hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-stone-100 text-stone-700">
            <FileText size={18} />
          </div>
          <div className="text-left">
            <p className="font-display text-xl">AI Resume Builder</p>
            <p className="text-sm text-stone-500 mt-0.5">Generate a polished, ATS-optimised version of your resume using the uploaded content</p>
          </div>
        </div>
        <ChevronDown size={18} className={`text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-8 pb-8 border-t border-stone-100">
          <button
            onClick={handleBuild}
            disabled={loading}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 font-medium"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Building your resume…" : "Generate improved resume"}
          </button>
          {built && (
            <div className="mt-6">
              <ResumePreview structured={built} targetRole={targetRole} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
